import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Package, Shapes } from 'lucide-react';
import type { ChatMessageDesignAction } from '@mg/shared';
import { designToolLabel } from '@mg/shared';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PostGenerateDesignModal } from '../../features/design/PostGenerateDesignModal';
import { DesignToolImportModal } from '../../features/media/DesignToolImportModal';
import { useDesignTools } from '../../features/media/useDesignTools';

/**
 * The Strategist's design hand-off card.
 *
 * What it deliberately does NOT offer is a button that writes into the user's Canva or Figma
 * account on their behalf. Figma's API cannot create design content at all, and Canva's template
 * autofill is Enterprise-gated — so a card promising that would be lying about the outcome. The
 * offer is a filled-in starting point: artboards carrying the copy, images and brand, which the
 * user opens and edits. That is also consistent with how this Strategist already behaves elsewhere
 * (it refuses to save copy the user has not seen).
 */
export function DesignActionCard({ action }: { action: ChatMessageDesignAction }) {
  const [bundleOpen, setBundleOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { tool: configuredTool, label: configuredLabel } = useDesignTools();

  // The bundle is built from the SAVED post, so it is fetched rather than reconstructed from the
  // conversation — what gets exported must be what is actually stored.
  const post = useQuery({
    queryKey: ['post', action.postId],
    queryFn: ({ signal }) => api.getPost(action.postId!, signal),
    enabled: bundleOpen && Boolean(action.postId),
  });

  const isImport = action.target === 'canva' || action.target === 'figma';
  // An import card is only actionable if that tool is actually the configured one.
  const importable = isImport && configuredTool === action.target;

  return (
    <div className="mt-2 rounded-xl border border-line bg-tint/60 p-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-card text-action shadow-sm">
          {isImport ? (
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          ) : (
            <Package className="h-4 w-4" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">{action.title}</p>
          {action.summary && <p className="mt-0.5 text-sm text-ink-label">{action.summary}</p>}

          {action.steps && action.steps.length > 0 && (
            <ul className="mt-2 space-y-1">
              {action.steps.map((step, i) => (
                <li key={i} className="text-xs text-ink-muted">
                  <span className="font-medium text-ink-strong">{step.label}</span>
                  {step.detail ? ` — ${step.detail}` : ''}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {isImport ? (
              importable ? (
                <Button size="sm" onClick={() => setImportOpen(true)}>
                  Import from {designToolLabel[action.target as 'canva' | 'figma']}
                </Button>
              ) : (
                <p className="text-xs text-ink-muted">
                  {designToolLabel[action.target as 'canva' | 'figma']} isn’t connected for this
                  workspace{configuredTool ? ` — ${configuredLabel} is.` : '.'} An admin can set it
                  up under Site Integrations.
                </p>
              )
            ) : action.postId ? (
              <Button size="sm" onClick={() => setBundleOpen(true)}>
                <Shapes className="h-4 w-4" aria-hidden />
                Generate design
              </Button>
            ) : (
              <p className="text-xs text-ink-muted">
                Save this as a draft first — the design is built from the saved post’s copy and
                images.
              </p>
            )}
          </div>
        </div>
      </div>

      <PostGenerateDesignModal
        post={bundleOpen ? (post.data ?? null) : null}
        onClose={() => setBundleOpen(false)}
      />
      {importable && configuredTool && (
        <DesignToolImportModal
          tool={configuredTool}
          open={importOpen}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}
